import { useEffect } from "react";
import { ArrowLeft, Mail, MessageCircle, MapPin, Phone, ShieldCheck } from "lucide-react";

const SUPPORT_EMAIL = "info@dhokiyas.ae";
const SUPPORT_WHATSAPP = "+91 75750 65656";
const COMPANY = "Dhokiya's Research Analyst (OPC) Private Limited";
const ADDRESS = "401-402, Bluestone Complex, Near Shyamdham Mandir, Sarthana Jakatnaka, Surat-395006, Gujarat, India";
const CIN = "U66190GJ2024OPC148079";
const GST = "24AAKCD5974H1ZC";
const SITE = "https://prop.dhokiyas.ae";
const EFFECTIVE = "1 January 2026";

function Shell({ title, eyebrow, children, testid }) {
  useEffect(() => { document.title = `${title} · Prop Passer Gold EA`; window.scrollTo(0, 0); }, [title]);
  return (
    <div data-testid={testid} className="relative min-h-screen overflow-hidden">
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[760px] h-[760px] gold-ring rounded-full pointer-events-none" />
      <div className="absolute inset-0 grain opacity-40 pointer-events-none" />
      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <a href="/" data-testid="legal-back-home"
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest font-mono text-[color:var(--ink-mute)] hover:text-[color:var(--gold)] transition-colors mb-8">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to homepage
        </a>
        <div className="font-mono text-[10px] sm:text-[11px] uppercase tracking-widest text-[color:var(--gold)] mb-3">{eyebrow}</div>
        <h1 className="font-display font-black text-3xl sm:text-5xl leading-[1.05] tracking-tight">{title}</h1>
        <div className="mt-3 font-mono text-[11px] uppercase tracking-widest text-[color:var(--ink-mute)]">Effective {EFFECTIVE}</div>
        <div className="mt-8 space-y-6 text-[14px] sm:text-[15px] leading-relaxed text-[color:var(--ink-dim)]">
          {children}
        </div>
        <div className="mt-12 pt-6 border-t border-[#23232a] flex items-center gap-2 text-[11px] text-[color:var(--ink-mute)]">
          <ShieldCheck className="w-3.5 h-3.5 text-[color:var(--gold)]" />
          {COMPANY}
        </div>
      </div>
    </div>
  );
}

const H = ({ children }) => <h2 className="font-display font-bold text-white text-lg sm:text-xl mt-8 mb-2">{children}</h2>;
const P = ({ children }) => <p>{children}</p>;
const Ul = ({ items }) => (
  <ul className="space-y-2 list-none">
    {items.map((it, i) => (
      <li key={i} className="flex gap-3">
        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[color:var(--gold)] shrink-0" />
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

// ---------- /contact ----------
export function Contact() {
  return (
    <Shell title="Contact us" eyebrow="Get in touch" testid="contact-page">
      <P>Questions about <strong className="text-white">Dhokiyas Prop Passer Gold EA</strong>, your purchase, your license,
        or general support — reach out using any of the channels below. We respond Monday–Saturday, 10:00–19:00 GST (UTC+4).</P>

      <div className="grid sm:grid-cols-2 gap-4 mt-4">
        <a href={`mailto:${SUPPORT_EMAIL}`} data-testid="contact-email"
           className="panel p-5 hover:border-[color:var(--gold)]/40 transition-colors group">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: "linear-gradient(135deg, rgba(245,208,106,.18), rgba(138,106,21,.05))", border: "1px solid rgba(231,180,59,.35)" }}>
              <Mail className="w-5 h-5 text-[color:var(--gold)]" />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--ink-mute)]">Email</div>
              <div className="font-display font-bold text-white">{SUPPORT_EMAIL}</div>
            </div>
          </div>
        </a>
        <a href={`https://wa.me/917575065656`} target="_blank" rel="noreferrer" data-testid="contact-whatsapp"
           className="panel p-5 hover:border-[color:var(--gold)]/40 transition-colors group">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: "linear-gradient(135deg, rgba(0,255,136,.18), rgba(0,180,90,.05))", border: "1px solid rgba(0,255,136,.35)" }}>
              <MessageCircle className="w-5 h-5 text-[color:var(--green)]" />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--ink-mute)]">WhatsApp</div>
              <div className="font-display font-bold text-white">{SUPPORT_WHATSAPP}</div>
            </div>
          </div>
        </a>
        <div className="panel p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: "linear-gradient(135deg, rgba(245,208,106,.18), rgba(138,106,21,.05))", border: "1px solid rgba(231,180,59,.35)" }}>
              <Phone className="w-5 h-5 text-[color:var(--gold)]" />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--ink-mute)]">Phone</div>
              <div className="font-display font-bold text-white">{SUPPORT_WHATSAPP}</div>
            </div>
          </div>
        </div>
        <div className="panel p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: "linear-gradient(135deg, rgba(245,208,106,.18), rgba(138,106,21,.05))", border: "1px solid rgba(231,180,59,.35)" }}>
              <MapPin className="w-5 h-5 text-[color:var(--gold)]" />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--ink-mute)]">Registered office</div>
              <div className="text-white text-[13.5px] leading-snug">{COMPANY}<br/>{ADDRESS}<br/>CIN: {CIN} · GST: {GST}</div>
            </div>
          </div>
        </div>
      </div>

      <H>Pre-sales questions</H>
      <P>If you're evaluating the EA, check our <a href="/#faq" className="text-[color:var(--gold)] underline">FAQ</a> first,
        then WhatsApp us — we usually reply within minutes during business hours.</P>

      <H>Setup & license issues</H>
      <P>For installation help, license re-issue, or MT5 binding errors, WhatsApp is fastest. Please share your license
        ID (format <code>DPPG-XXXX-XXXX-XXXX</code>) and a screenshot of the MT5 Experts tab.</P>

      <H>Business / partnerships</H>
      <P>For affiliate, prop-firm partnerships, or media enquiries, email <a href={`mailto:${SUPPORT_EMAIL}`}
         className="text-[color:var(--gold)] underline">{SUPPORT_EMAIL}</a> with the subject line "Partnership".</P>
    </Shell>
  );
}

// ---------- /terms ----------
export function Terms() {
  return (
    <Shell title="Terms of Service" eyebrow="Legal" testid="terms-page">
      <P>These Terms govern your use of <strong className="text-white">{SITE}</strong> and the
        Dhokiyas Prop Passer Gold EA software (the "EA"). By purchasing, downloading, or using the EA, you accept
        these Terms in full.</P>

      <H>1. The product</H>
      <P>The EA is an algorithmic trading tool ("Expert Advisor") for the MetaTrader 5 platform. It is sold as a
        single-purchase, lifetime-license digital product, valid for up to 10 MT5 accounts per license key.</P>

      <H>2. License grant</H>
      <P>Subject to payment, we grant you a non-exclusive, non-transferable, revocable license to install and use
        one (1) license key on up to ten (10) of your own MT5 accounts. You may not:</P>
      <Ul items={[
        "Resell, redistribute, sublicense, lease, lend, or share the EA, license file, or any part of it.",
        "Reverse-engineer, decompile, or attempt to extract the source from the .ex5 file.",
        "Remove or modify the in-EA license-validation routine (DPPG_LicenseGuard).",
        "Use the EA to operate trading services for third parties without prior written consent.",
      ]} />

      <H>3. Anti-piracy</H>
      <P>The EA contains a server-side license check on first run for each MT5 account. We retain the right to
        block any license found to be shared, leaked, or used in violation of these Terms. Blocked licenses are
        not eligible for refund.</P>

      <H>4. Trading risk &amp; no warranties</H>
      <P>Trading foreign exchange, gold (XAUUSD), and any leveraged instrument carries substantial risk of loss
        and is not suitable for all investors. The EA is a tool, not advice. Past performance does not guarantee
        future results. <strong className="text-white">No expert advisor can guarantee profit or guarantee passing
        any prop-firm challenge.</strong> You are solely responsible for your trading decisions and outcomes.</P>
      <P>The EA is provided "AS IS" without warranties of any kind, express or implied, including merchantability,
        fitness for a particular purpose, or non-infringement.</P>

      <H>5. Limitation of liability</H>
      <P>To the maximum extent permitted by law, our aggregate liability arising out of or in connection with the
        EA shall not exceed the amount you paid for the license. We are not liable for any trading losses,
        prop-firm-account losses, indirect, consequential, incidental, or punitive damages.</P>

      <H>6. Updates &amp; support</H>
      <P>Free version updates are included for the life of the product. Customer support is provided on a
        best-effort basis via email and WhatsApp during business hours.</P>

      <H>7. Modification &amp; termination</H>
      <P>We may update these Terms from time to time. Continued use of the EA after changes are posted constitutes
        acceptance. We may terminate your license for material breach (piracy, abuse, chargeback fraud).</P>

      <H>8. Governing law</H>
      <P>These Terms are governed by the laws of the Republic of India. Any dispute shall be resolved exclusively
        in the competent courts of Surat, Gujarat, India.</P>

      <H>9. Contact</H>
      <P>{COMPANY}<br/>{ADDRESS}<br/>CIN: {CIN} · GST: {GST}<br/>Email: <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[color:var(--gold)] underline">{SUPPORT_EMAIL}</a> · WhatsApp / Phone: {SUPPORT_WHATSAPP}</P>
    </Shell>
  );
}

// ---------- /privacy ----------
export function Privacy() {
  return (
    <Shell title="Privacy Policy" eyebrow="Legal" testid="privacy-page">
      <P>This Privacy Policy explains what data we collect, why we collect it, and your rights. We aim to collect
        only the minimum information needed to deliver your purchase and run the website.</P>

      <H>1. Data we collect</H>
      <Ul items={[
        "Account / order data: name, email, WhatsApp/phone number (optional), payment ID, country.",
        "MT5 binding data: account number, broker name, terminal name (collected only when the EA first activates on each MT5 account, used solely to enforce the 10-account license limit).",
        "Technical data: IP address, browser user-agent, referrer, language. Used for fraud prevention and analytics.",
        "Marketing data: page views, scroll depth, clicks (only if you do not block trackers).",
      ]} />

      <H>2. How we use it</H>
      <Ul items={[
        "Deliver your license, EA file, setup PDF, and post-purchase email/WhatsApp confirmations.",
        "Operate the in-EA license-validation system that prevents piracy.",
        "Provide customer support and respond to your questions.",
        "Send transactional and (with your consent) marketing communications about updates, new features, and offers.",
        "Comply with legal, tax, and accounting obligations.",
      ]} />

      <H>3. Third-party processors</H>
      <P>We share data only with vetted processors who help us run the service:</P>
      <Ul items={[
        "Cashfree Payments (India INR transactions) — payment processing.",
        "TagMango (Global USD transactions) — payment processing & order webhooks.",
        "Zoho Mail (smtp.zoho.in) — email delivery.",
        "AiSensy — WhatsApp message delivery.",
        "Telegram (Telegram FZ-LLC) — internal sales notifications only (no customer messages).",
        "Hostinger / cloud-hosting providers — server infrastructure.",
        "Meta Pixel, Google Analytics, TikTok Pixel — anonymised conversion analytics (you can block these via your browser or ad-blocker).",
        "ipapi.co — country-detection for currency/payment routing (IP only, not stored).",
      ]} />

      <H>4. Data retention</H>
      <P>License records are retained for the life of the license (lifetime) so you can re-download and re-bind
        accounts. Order and tax records are retained as required by Indian accounting and GST law (typically 6–8
        years). Marketing data is anonymised after 24 months.</P>

      <H>5. Your rights</H>
      <P>You have the right to access, correct, export, or delete your personal data. To exercise any of these
        rights, email <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[color:var(--gold)] underline">{SUPPORT_EMAIL}</a>.
        Note: deleting your license record will permanently disable your EA on all bound MT5 accounts and is
        irreversible.</P>

      <H>6. Cookies</H>
      <P>We use a small number of essential cookies (session, language preference, currency choice) and
        analytics/marketing cookies (Meta, Google, TikTok). You can disable them via browser settings or an
        ad-blocker — the site will continue to function.</P>

      <H>7. Security</H>
      <P>Payment card data never touches our servers — it is handled exclusively by Cashfree / TagMango on PCI-DSS
        infrastructure. License files are HMAC-SHA256 signed. All site traffic is served over HTTPS (TLS 1.2+).</P>

      <H>8. Children</H>
      <P>The EA is not directed at, and we do not knowingly collect data from, persons under 18.</P>

      <H>9. Changes</H>
      <P>We may update this policy from time to time. Material changes will be flagged on the website.</P>

      <H>10. Contact</H>
      <P>{COMPANY}<br/>{ADDRESS}<br/>CIN: {CIN} · GST: {GST}<br/>Email: <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[color:var(--gold)] underline">{SUPPORT_EMAIL}</a> · WhatsApp / Phone: {SUPPORT_WHATSAPP}</P>
    </Shell>
  );
}

// ---------- /refund-cancellation ----------
export function RefundCancellation() {
  return (
    <Shell title="Refund & Cancellation Policy" eyebrow="Legal" testid="refund-page">
      <P>We stand behind Dhokiyas Prop Passer Gold EA with a clear, honest refund policy. Read this page carefully
        before purchasing — by completing payment you accept these terms.</P>

      <H>1. 14-day money-back guarantee</H>
      <P>If the EA fails to install correctly, doesn't work on a clean MT5 setup, or is materially different from
        what's described on this site, you may request a full refund within <strong className="text-white">14
        calendar days</strong> of purchase.</P>
      <P>To request a refund, email <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[color:var(--gold)] underline">{SUPPORT_EMAIL}</a>{" "}
        with the subject line "Refund request" and include:</P>
      <Ul items={[
        "Your order email and license ID (DPPG-XXXX-XXXX-XXXX).",
        "Payment receipt / transaction ID (Cashfree or TagMango).",
        "Reason for the refund and, where applicable, screenshots showing the issue.",
      ]} />

      <H>2. What is NOT eligible for refund</H>
      <Ul items={[
        "Trading losses incurred while running the EA on a live or prop-firm account. The EA is a tool — it does not guarantee profit and cannot guarantee passing any prop-firm challenge.",
        "Failure of a prop-firm challenge or a funded-account breach. The EA enforces drawdown caps but cannot override a challenge's specific consistency or news rules — please read the EA User Guide before deploying.",
        "Disagreement with the EA's strategy, signal frequency, or trade outcomes after you have used it for more than 14 days.",
        "Requests made after 14 days from the original purchase date.",
        "Licenses found to be shared, leaked, or used in violation of our Terms of Service.",
        "Chargebacks initiated without first contacting us. We attempt to resolve every dispute directly and may dispute fraudulent chargebacks.",
      ]} />

      <H>3. Cancellation</H>
      <P>The EA is a one-time-payment lifetime license. There is no recurring subscription to cancel. If you
        no longer wish to use the EA, simply uninstall it from MT5 and remove the license file. Your data can be
        deleted on request — see the <a href="/privacy" className="text-[color:var(--gold)] underline">Privacy
        Policy</a>.</P>

      <H>4. Refund processing time</H>
      <P>Approved refunds are processed within <strong className="text-white">7–10 business days</strong> back to
        the original payment method via the same gateway used for purchase (Cashfree or TagMango). Bank-side
        settlement may take an additional 3–5 business days depending on your card issuer.</P>

      <H>5. License revocation on refund</H>
      <P>Once a refund is approved, the associated license is permanently blocked across all bound MT5 accounts.
        The EA will stop trading on those accounts and the binding cannot be restored later.</P>

      <H>6. Contact for refund requests</H>
      <P>{COMPANY}<br/>{ADDRESS}<br/>CIN: {CIN} · GST: {GST}<br/>Email: <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[color:var(--gold)] underline">{SUPPORT_EMAIL}</a> · WhatsApp / Phone: {SUPPORT_WHATSAPP}</P>
    </Shell>
  );
}
